// src/features/capacity/capacityUrl.js
//
// URL contract for /places/capacity. Everything shareable lives in the query
// string; neutral/default values are omitted so the canonical URL is short.
// Retired Pressure/Scenario parameters (horizon, basis, layer, whatif, tab)
// are dropped deliberately; legacy expandMarket becomes `open`.

import { LEVERS, NEUTRAL_LEVERS } from "./capacityModel.js";

// "issue" (earliest first issue) is the default; explicit choices persist.
export const DEFAULT_SORT = "issue";
export const SORT_KEYS = ["issue", "market", "people", "seats", "pressure"];
export const STATUS_FILTERS = ["over", "densify", "tight", "comfortable", "no_data", "history"];
const LEVER_PARAM = { growth: "g", days: "days", sharing: "share", hold: "hold", conf: "conf", waste: "waste" };

function validLever(lever, raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < lever.min - 1e-9 || n > lever.max + 1e-9) return null;
  const steps = (n - lever.min) / lever.step;
  if (Math.abs(steps - Math.round(steps)) > 1e-6) return null;
  return Math.round(n * 10) / 10;
}
const list = (raw) => (raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : []);

export function parseCapacityState(search = "") {
  const p = new URLSearchParams(search);
  const levers = { ...NEUTRAL_LEVERS };
  for (const l of LEVERS) {
    const v = validLever(l, p.get(LEVER_PARAM[l.key]));
    if (v != null) levers[l.key] = v;
  }
  const focus = /^\d{4}-(0[1-9]|1[0-2])$/.test(p.get("focus") || "") ? p.get("focus") : null;
  const sort = SORT_KEYS.includes(p.get("sort")) ? p.get("sort") : DEFAULT_SORT;
  const open = [...new Set([...list(p.get("open")), ...list(p.get("expandMarket"))])];
  return {
    view: p.get("view") === "quarterly" ? "quarterly" : "monthly",
    focus,
    panel: p.get("panel") === "scenario" ? true : p.get("panel") === "closed" ? false : null,
    levers,
    q: (p.get("q") || "").slice(0, 120),
    region: list(p.get("region")),
    status: list(p.get("status")).filter((s) => STATUS_FILTERS.includes(s)),
    sort,
    dir: p.get("dir") === "desc" ? "desc" : "asc",
    open,
  };
}

export function serializeCapacityState(state) {
  const p = new URLSearchParams();
  if (state.view === "quarterly") p.set("view", "quarterly");
  if (state.focus) p.set("focus", state.focus);
  if (state.panel === true) p.set("panel", "scenario");
  if (state.panel === false) p.set("panel", "closed");
  for (const l of LEVERS) {
    const v = state.levers?.[l.key];
    if (v != null && Math.abs(v - l.neutral) > 1e-9) p.set(LEVER_PARAM[l.key], String(v));
  }
  if (state.q) p.set("q", state.q);
  if (state.region?.length) p.set("region", state.region.join(","));
  if (state.status?.length) p.set("status", state.status.join(","));
  if (state.sort && state.sort !== DEFAULT_SORT) p.set("sort", state.sort);
  if (state.dir === "desc") p.set("dir", "desc");
  if (state.open?.length) p.set("open", state.open.join(","));
  const s = p.toString();
  return s ? `?${s}` : "";
}

// Old /places/pressure and /places/scenario links: old Scenario links always
// meant "panel open"; everything else round-trips through the parser.
export function normalizeCapacitySearch(search = "", { fromLens = null } = {}) {
  const state = parseCapacityState(search);
  if (fromLens === "scenario") state.panel = true;
  return serializeCapacityState(state);
}
